import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowLeft, 
  Lock, 
  Eye, 
  EyeOff, 
  CheckCircle2, 
  Globe,
  Mail,
  Smartphone,
  LockKeyhole,
  ShieldCheck,
  Building2, // Added for company dropdown icon
  Building
} from 'lucide-react';
import styles from './MemberRegistration.module.css';
import Logo from '../../Assets/coop-logo.svg'
import SecureLogo from '../../Assets/secure.png'
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BASE_URL } from '../../Components/Api/Api';
import Swal from 'sweetalert2';
import { BRANDING } from '../../Components/Branding/Branding';

export default function MemberRegistration() {
  const [step, setStep] = useState(1); // 1 = Email, 2 = OTP, 3 = Password
  
  // Form States
  const [email, setEmail] = useState('');
  const [companies, setCompanies] = useState([]); // Stores fetched companies
  const [companyId, setCompanyId] = useState(''); // Stores selected company ID
  const [companiesLoading, setCompaniesLoading] = useState(false); // Mid-screen loader trigger
  const [emailError, setEmailError] = useState(''); // Direct red error text under input

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // UI States
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [timer, setTimer] = useState(165); 
  const [language, setLanguage] = useState('English');
  
  const otpRefs = useRef([]);
  const currentYear = new Date().getFullYear();

  // Live validation checks
  const validationRules = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    specialChar: /[@$!%*?&]/.test(password),
  };

  const getPasswordStrength = () => {
    const passedCount = Object.values(validationRules).filter(Boolean).length;
    if (!password) return { text: 'None', blocks: 0, color: '' };
    if (passedCount <= 2) return { text: 'Weak', blocks: 1, color: styles.strengthWeak };
    if (passedCount <= 4) return { text: 'Medium', blocks: 3, color: styles.strengthMedium };
    return { text: 'Strong', blocks: 4, color: styles.strengthStrong };
  };

  const strength = getPasswordStrength();

  // Timer effect for OTP step
  useEffect(() => {
    if (step !== 2) return;
    const interval = setInterval(() => {
      setTimer((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

  // Effect to automatically fetch companies when email is complete/valid
  useEffect(() => {
  // Regex that accepts standard domains (.com, .co) as well as multi-part TLDs (.co.uk, .com.ng)
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})?$/;
  
  if (emailRegex.test(email)) {
    fetchCompanies(email);
  } else {
    // Clear states if email becomes incomplete/invalid
    setCompanies([]);
    setCompanyId('');
    setEmailError('');
  }
}, [email]);

  // Function to fetch registered companies
  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    setEmailError('');
    try {
      const response = await axios.get(`${BASE_URL}/my-coop-company?email=${email}`,);
      
      // Adjust this extraction matching your backend response payload structure
      const companyList = response.data?.companies || response.data?.data || [];
      
      setCompanies(companyList);

if (companyList.length > 0) {
  setEmailError('');
  setCompanyId(companyList[0]?.id || '');
} else {
  setCompanyId('');
  setEmailError(response.data?.message || 'No company found.');
}
    } catch (error) {
       let errorMessage = 'An error occurred. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
        if (typeof message === 'string') errorMessage = message;
        else if (Array.isArray(message)) errorMessage = message.join('; ');
        else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          errorMessage = Array.isArray(messages) ? messages.join('; ') : messages;
        }
      }
      setEmailError(errorMessage);
      setCompanies([]);
      setCompanyId('');
    } finally {
      setCompaniesLoading(false);
    }
  };

  const handleOtpChange = (value, index) => {
    if (isNaN(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1].focus();
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1].focus();
    }
  };

  const handle1stStep = async () => {
    if (!companyId) {
      Swal.fire({
        icon: 'warning',
        title: 'Selection Required',
        text: 'Please select a company to proceed.',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/member-auth/signup`, {
        email: email,
        company_id: companyId // Passing company_id along to fetch OTP
      });
      console.log(response.data);
      setStep(2);
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
    
        if (typeof message === 'string') {
          errorMessage = message;
        } else if (Array.isArray(message)) {
          errorMessage = message.join('; ');
        } else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          if (Array.isArray(messages)) {
            errorMessage = messages.join('; ');
          } else {
            errorMessage = messages;
          }
        }
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handle2ndStep = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/member-auth/verify-otp`, {
        email: email,
        otp: otp.join(''),
        company_id: companyId
      });
      console.log(response.data);
      setStep(3);
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
        if (typeof message === 'string') {
          errorMessage = message;
        } else if (Array.isArray(message)) {
          errorMessage = message.join('; ');
        } else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          if (Array.isArray(messages)) {
            errorMessage = messages.join('; ');
          } else {
            errorMessage = messages;
          }
        }
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handle3rdStep = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${BASE_URL}/member-auth/create-password`, {
        email: email,
        password: password,
        password_confirmation: confirmPassword,
        company_id: companyId 
      });
      console.log(response.data);
      navigate('/');
    } catch (error) {
      let errorMessage = 'An error occurred. Please try again.';
      if (error.response && error.response.data && error.response.data.message) {
        const { message } = error.response.data;
        if (typeof message === 'string') {
          errorMessage = message;
        } else if (Array.isArray(message)) {
          errorMessage = message.join('; ');
        } else if (typeof message === 'object') {
          const firstKey = Object.keys(message)[0];
          const messages = message[firstKey];
          if (Array.isArray(messages)) {
            errorMessage = messages.join('; ');
          } else {
            errorMessage = messages;
          }
        }
        Swal.fire({
          icon: 'error',
          title: 'Oops...',
          text: errorMessage,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.screenWrapper}>
      
      {/* GLOBAL MID-SCREEN LOADER OVERLAY */}
      {companiesLoading && (
        <div className={styles.globalLoaderOverlay}>
          <div className={styles.spinnerContainer}>
            <div className={styles.spinner}></div>
            <p className={styles.loadingText}>Fetching registered companies...</p>
          </div>
        </div>
      )}

      {/* LEFT COLUMN: STATIC BRAND SIDEBAR */}
      <div className={styles.leftSidebar}>
        <div className={styles.brandTopArea}>
          <img src={BRANDING?.coopPrimary?.logo} alt="RCCG Logo" className={styles.logoImg} />
        </div>
        
        <div className={styles.sidebarContent}>
          <h1>Welcome to Your<br /><span className={styles.greenText}>Member Portal</span></h1>
          <div className={styles.shortDivider}></div>
          <p>You're just one step away from accessing your cooperative account. Please follow the verification prompts to secure your profile.</p>
        </div>

        <div className={styles.illustrationCenter}>
          <img src={SecureLogo} alt="Secure Illustration" style={{objectFit: 'contain', height: 200, width: 200}} />
        </div>

        <p className={styles.copyrightText}>© {currentYear} {BRANDING?.coopPrimary?.mainName}. All rights reserved.</p>
      </div>

      {/* RIGHT COLUMN: INTERACTIVE VIEW PORTAL */}
      <div className={styles.rightContentArea}>
        
        <div className={styles.topUtilityRow}>
          <button 
            onClick={() => step > 1 && setStep(step - 1)}
            disabled={step === 1}
            className={`${styles.navBackBtn} ${step === 1 ? styles.invisible : ''}`}
          >
            <ArrowLeft size={16} /> Back
          </button>
          
          <div className={styles.langSelector}>
            <Globe size={14} />
            <select value={language} onChange={(e) => setLanguage(e.target.value)}>
              <option value="English">English</option>
            </select>
          </div>
        </div>

        <div className={styles.portalActionCard}>
          
          {/* STEP 1: EMAIL REQUEST & COMPANY SELECT */}
          {step === 1 && (
            <form onSubmit={(e) => {
              e.preventDefault();
              handle1stStep();
            }} className={styles.formSpace}>
              <div className={styles.cardHeaderArea}>
                <div className={styles.lockIconCircle}><Mail size={24} /></div>
                <h3>Verify Your Email</h3>
                <p>Provide your registered email address to receive your confirmation security OTP.</p>
              </div>

              <div className={styles.cleanInputGroup}>
                <label>Email Address</label>
                <div className={styles.fieldInteractiveWrapper}>
                  <Mail className={styles.startFieldIcon} size={18} />
                  <input 
                    type="email" 
                    required 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com" 
                    className={styles.cleanInput} 
                  />
                </div>
                {/* Red sub-label error text if no registered company is found */}
                {companies.length === 0 && emailError && (
    <span className={styles.errorMessageText}>
        {emailError}
    </span>
)}
              </div>
              

              {/* Conditionally Rendered Company Select dropdown */}
              {companies.length > 0 && (
                <div className={styles.cleanInputGroup}>
                <label>Select Company</label>
                <div className={styles.fieldInteractiveWrapper}>
                  <Building2 className={styles.startFieldIcon} size={18} />
                  <select
                      required
                      value={companyId}
                      onChange={(e) => setCompanyId(e.target.value)}
                      className={styles.cleanInput}
                    >
                      <option  value="">
                         Select your company
                        </option>
                      {companies.map((co) => (
                        <option key={co.id} value={co.id}>
                          {co.name || co.company_name}
                        </option>
                      ))}
                    </select>
                </div>
                
                </div>
              )}

              <button
                type="submit"
                className={styles.submitActionBtn}
                disabled={loading || companies.length === 0}
              >
                <LockKeyhole color="white" />
                {loading ? "Sending OTP..." : "Send OTP"}
              </button>
            </form>
          )}

          {/* STEP 2: OTP CODES ENTER */}
          {step === 2 && (
            <form onSubmit={(e) => { e.preventDefault(); handle2ndStep(); }} className={styles.formSpace}>
              <div className={styles.cardHeaderArea}>
                <div className={styles.lockIconCircle}><Smartphone size={24} /></div>
                <h3>Verify your Identity</h3>
                <p className={styles.stepSubheading}>We have sent a 6-digit OTP to your registered email address</p>
              </div>

              <div className={styles.emailPill}>
                <div className={styles.pillIcon}><Mail size={12} /></div>
                <span>{email.replace(/(.{3})(.*)(?=@)/, "$1***")}</span>
              </div>

              <div className={styles.otpGrid}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength={1}
                    value={digit}
                    ref={(el) => (otpRefs.current[index] = el)}
                    onChange={(e) => handleOtpChange(e.target.value, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    className={`${styles.otpInput} ${digit ? styles.otpInputFilled : ''}`}
                  />
                ))}
              </div>
              
              <div className={styles.timerWrapper}>
                Code expires in: <span className={styles.greenText}>{Math.floor(timer/60)}:{(timer%60).toString().padStart(2,'0')}</span>
              </div>

              <div className={styles.securityCard}>
                <div className={styles.securityEmblem}>
                  <ShieldCheck className={styles.securityCardIcon} size={24} />
                </div>
                <div>
                  <h4 className={styles.securityTitle}>For your security</h4>
                  <p className={styles.securityDescription}>Do not share this code with anyone</p>
                </div>
              </div>

              <button disabled={loading} type="submit" className={styles.submitActionBtn}>
                <LockKeyhole color='white' style={{borderWidth: 0.5}}/> {loading ? 'Verifying...' : 'Verify OTP'}
              </button>
            </form>
          )}

          {/* STEP 3: NEW PASSWORD */}
          {step === 3 && (
            <form onSubmit={(e) => { e.preventDefault(); handle3rdStep(); }} className={styles.formSpace}>
              <div className={styles.cardHeaderArea}>
                <div className={styles.lockIconCircle}>
                  <Lock size={24} />
                  <div className={styles.miniCheckCheck}>✓</div>
                </div>
                <h3>Create Password</h3>
                <p>Create a strong password for your account to keep your information secure.</p>
              </div>

              <div className={styles.cleanInputGroup}>
                <label>New Password</label>
                <div className={styles.fieldInteractiveWrapper}>
                  <Lock className={styles.startFieldIcon} size={18} />
                  <input
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={styles.cleanInput}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className={styles.endFieldIcon}>
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className={styles.strengthMeterContainer}>
                <div className={styles.strengthBarsRow}>
                  {[1, 2, 3, 4].map((barIndex) => (
                    <div 
                      key={barIndex} 
                      className={`${styles.strengthBarBase} ${barIndex <= strength.blocks ? strength.color : ''}`}
                    />
                  ))}
                </div>
                <span className={`
                  ${styles.strengthLabelText} 
                  ${strength.text === 'Weak' || strength.blocks === 1 ? styles.textWeak : ''}
                  ${strength.text === 'Medium' || strength.blocks === 2 || strength.blocks === 3 ? styles.textMedium : ''}
                  ${strength.text === 'Strong' || strength.blocks === 4 ? styles.textStrong : ''}
                `}>
                  {strength.text || 'None'}
                </span>
              </div>
              <p className={styles.helperTipText}>Use 8 or more characters with a mix of letters, numbers and symbols.</p>

              <div className={styles.cleanInputGroup}>
                <label>Confirm Password</label>
                <div className={styles.fieldInteractiveWrapper}>
                  <Lock className={styles.startFieldIcon} size={18} />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={styles.cleanInput}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className={styles.endFieldIcon}>
                    {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className={styles.requirementsCheckboxBlock}>
                <h5>Password must contain:</h5>
                <ul className={styles.requirementsCheckList}>
                  <li className={validationRules.length ? styles.validRule : styles.invalidRule}>
                    <CheckCircle2 size={14} /> At least 8 characters
                  </li>
                  <li className={validationRules.uppercase ? styles.validRule : styles.invalidRule}>
                    <CheckCircle2 size={14} /> Uppercase (A-Z)
                  </li>
                  <li className={validationRules.lowercase ? styles.validRule : styles.invalidRule}>
                    <CheckCircle2 size={14} /> Lowercase (a-z)
                  </li>
                  <li className={validationRules.number ? styles.validRule : styles.invalidRule}>
                    <CheckCircle2 size={14} /> Number (0-9)
                  </li>
                  <li className={validationRules.specialChar ? styles.validRule : styles.invalidRule}>
                    <CheckCircle2 size={14} /> Special character (@$!%*?&)
                  </li>
                </ul>
              </div>

              <button disabled={loading} type="submit" className={styles.submitActionBtn}>
                <LockKeyhole color='white' style={{borderWidth: 0.5}}/>{loading ? 'Creating Password...' : 'Create Password'}
              </button>

              <p className={styles.legalDisclaimer}>
                By creating a password, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>.
              </p>
            </form>
          )}

          <div className={styles.supportCenterFooter}>
            Need help? <a href="tel:+2348161241827">Contact our support team</a> 🎧
          </div>
          <div className={styles.supportCenterFooter1}>
            Remember your password? <span onClick={() => navigate('/')} style={{
              color: '#16a34a',
              cursor: 'pointer'
            }}>Sign in!</span>
          </div>

        </div>
      </div>
    </div>
  );
}